import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import type { ApiResponse } from "../types/auth";
import type { CandidateStatusEnum } from "../types/candidate";
import type {
  ProjectCandidateListItem,
  ProjectCandidatesApiResponse,
} from "../types/project";
import type { CandidateDetail } from "../types/candidate";

interface UpdateStatusPayload {
  /** CandidateStatus doc ID (NOT candidate_id) */
  id: string;
  status: CandidateStatusEnum;
  /** Project ID — used to invalidate the right query cache keys */
  projectId: string;
  /**
   * Candidate ID — used to invalidate the candidate detail query cache
   * (`["candidates", candidateId, ...]`) so the detail page reflects the
   * new status without a manual reload. Optional for backwards-compat
   * with call sites that don't yet pass it.
   */
  candidateId?: string;
}

interface CandidateStatusResponse {
  id: string;
  candidate_id: string;
  status: CandidateStatusEnum;
  [key: string]: unknown;
}

interface UseUpdateCandidateStatusReturn {
  updateStatus: (payload: UpdateStatusPayload) => Promise<void>;
  isUpdating: boolean;
}

export function useUpdateCandidateStatus(): UseUpdateCandidateStatusReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, status }: UpdateStatusPayload) => {
      const res = await apiClient.patch<ApiResponse<CandidateStatusResponse>>(
        `/candidate-statuses/${id}/status`,
        { status }
      );
      return res;
    },

    onMutate: async ({ id, status, projectId, candidateId }) => {
      // Cancel any in-flight fetches for the candidates list and detail
      await queryClient.cancelQueries({
        queryKey: ["projects", projectId, "candidates"],
      });
      if (candidateId) {
        await queryClient.cancelQueries({
          queryKey: ["candidates", candidateId],
        });
      }

      // Snapshot the previous value for rollback.
      // useProjectCandidates stores res.data (ProjectCandidatesApiResponse)
      // in the cache — not the outer ApiResponse wrapper.
      const previousData =
        queryClient.getQueriesData<ProjectCandidatesApiResponse>({
          queryKey: ["projects", projectId, "candidates"],
        });

      // Snapshot the candidate detail caches (one entry per jdPath variant).
      // CandidateDetailContent uses queryFn returning `{ data: CandidateDetail }`
      // and `select` to unwrap, so the cached shape is the raw API envelope.
      const previousDetail = candidateId
        ? queryClient.getQueriesData<{ data: CandidateDetail }>({
            queryKey: ["candidates", candidateId],
          })
        : [];

      // Optimistic update: modify the status in every cached page.
      // The cached value shape is ProjectCandidatesApiResponse:
      //   { data: ProjectCandidateListItem[], total, page, limit, total_pages }
      queryClient.setQueriesData<ProjectCandidatesApiResponse>(
        { queryKey: ["projects", projectId, "candidates"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((c: ProjectCandidateListItem) =>
              c.id === id ? { ...c, status } : c
            ),
          };
        }
      );

      // Optimistic update for the candidate detail cache so the profile
      // page dropdown reflects the new status immediately (otherwise the
      // stale `data.status` from the cached detail response wins over the
      // parent's local optimistic state).
      if (candidateId) {
        queryClient.setQueriesData<{ data: CandidateDetail }>(
          { queryKey: ["candidates", candidateId] },
          (old) => {
            if (!old?.data) return old;
            return { ...old, data: { ...old.data, status } };
          }
        );
      }

      return { previousData, previousDetail, projectId };
    },

    onError: (_err, _vars, context) => {
      // Roll back to the snapshot on error
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        for (const [queryKey, data] of context.previousDetail) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: (_data, _err, { projectId, candidateId }) => {
      // Always revalidate after mutation settles
      void queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "candidates"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["projects", projectId],
      });
      // Invalidate the candidate detail cache so the profile page picks
      // up the fresh status — the cache key includes jdPath, so we use a
      // prefix match on ["candidates", candidateId].
      if (candidateId) {
        void queryClient.invalidateQueries({
          queryKey: ["candidates", candidateId],
        });
      }
    },
  });

  return {
    updateStatus: async (payload) => {
      await mutation.mutateAsync(payload);
    },
    isUpdating: mutation.isPending,
  };
}
